// accordion-item.ts
import { LitElement, html, css } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

export interface AccordionItemData {
    id: string;
    title: string;
    icon?: string;
    children?: AccordionItemData[];
    onClick?: () => void;
    tooltip?: string;
}

@customElement('accordion-item')
export class AccordionItem extends LitElement {
    static styles = css`
        :host {
            display: block;
        }

        .accordion-item {
            width: 100%;
            font-family: 'Maersk Text';
            font-size: 13px;
            line-height: 24px;
            color: var(--neutrals-grey-03, black);
        }

        .accordion-header {
            display: flex;
            align-items: center;
            padding: 6px 12px;
            cursor: pointer;
            transition: background-color 0.2s ease;
            gap: 9px;
        }

        .accordion-header:hover {
            background-color: rgba(11, 115, 158, 0.13);
        }

        .accordion-header.clickable {
            cursor: pointer;
        }

        .accordion-header mc-icon::part(icon) {
            fill: var(--primary-blue-300, white);
        }

        .title {
            flex-grow: 1;
        }

        .arrow {
            transition: transform 0.2s ease;
        }

        .arrow.expanded {
            transform: rotate(180deg);
        }

        .children {
            overflow: hidden;
            max-height: 0;
            transition: max-height 0.3s ease-out;
        }

        .children.expanded {
            max-height: 1000px; /* arbitrary large value */
            transition: max-height 0.5s ease-in;
        }
    `

    @property({ type: Object }) item!: AccordionItemData
    @property({ type: Number }) level = 0
    @state() expanded = false

    toggle(e: Event) {
        if (this.item.onClick) {
            this.item.onClick()
        }

        if (this.item.children && this.item.children.length > 0) {
            this.expanded = !this.expanded
        }

        e.stopPropagation()
    }

    render() {
        const { title, icon, children, tooltip } = this.item
        const hasChildren = children && children.length > 0

        return html`
            <div class="accordion-item">
                <div
                    class="accordion-header"
                    style="padding-left: ${12 + this.level * 20}px"
                    @click="${(e: Event) => this.toggle(e)}"
                >
                    ${icon ? html`<mc-icon icon=${icon}></mc-icon>` : null}
                    <div class="title">${title}</div>
                    ${ tooltip ? html`
                        <voyage-tooltip-proxy
                            text=${tooltip}
                            html-content
                            .positionPriority=${["bottom"]}
                            .tooltipInteraction=${true}
                        >
                        </voyage-tooltip-proxy>
                        `: html``}
                    ${hasChildren
                        ? html`
                              <mc-icon
                                  class="arrow ${this.expanded
                                      ? 'expanded'
                                      : ''}"
                                  icon="chevron-down"
                              ></mc-icon>
                          `
                        : ''}
                </div>
                ${hasChildren
                    ? html`
                          <div
                              class="children ${this.expanded
                                  ? 'expanded'
                                  : ''}"
                          >
                              ${children?.map(
                                  (child) => html`
                                      <accordion-item
                                          .item="${child}"
                                          .level="${this.level + 1}"
                                      ></accordion-item>
                                  `,
                              )}
                          </div>
                      `
                    : ''}
            </div>
        `
    }
}
